import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { vectorLoaderService, type SearchResult, type Document } from '@/services/vector-loader.service';
import collectionsService, { type DocumentCollection } from '@/services/collections.service';
import { Loader2, Upload, Search, FileText, ChevronRight, Plus, Edit2, Trash2, Database, FileX2, ChevronLeft, ChevronRight as ChevronRightIcon, RefreshCw } from 'lucide-react';
import { useEffect } from 'react';
import useAuthStore from '@/utils/authStore';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';

export default function VectorLoaderPage() {
  const { selectedProjectId } = useAuthStore();
  
  // Collections state
  const [collections, setCollections] = useState<DocumentCollection[]>([]);
  const [isLoadingCollections, setIsLoadingCollections] = useState(false);
  const [collectionError, setCollectionError] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCollection, setEditingCollection] = useState<DocumentCollection | null>(null);
  const [collectionName, setCollectionName] = useState('');
  const [isSavingCollection, setIsSavingCollection] = useState(false);
  
  // Load chunks state
  const [textContent, setTextContent] = useState('');
  const [chunks, setChunks] = useState<string[]>([]);
  const [chunkMethod, setChunkMethod] = useState<'size' | 'delimiter'>('size');
  const [chunkSize, setChunkSize] = useState(500);
  const [chunkOverlap, setChunkOverlap] = useState(50);
  const [delimiter, setDelimiter] = useState('---');
  const [isLoading, setIsLoading] = useState(false);
  const [loadMessage, setLoadMessage] = useState('');
  const [loadError, setLoadError] = useState('');
  const [selectedLoadCollection, setSelectedLoadCollection] = useState<string>('');

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [topK, setTopK] = useState(5);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [selectedSearchCollection, setSelectedSearchCollection] = useState<string>('');

  // Documents state
  const [documents, setDocuments] = useState<Document[]>([]);
  const [documentsTotal, setDocumentsTotal] = useState(0);
  const [documentsPage, setDocumentsPage] = useState(1);
  const [documentsPerPage] = useState(10);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<Set<string>>(new Set());
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [isDeletingDocuments, setIsDeletingDocuments] = useState(false);
  const [documentsError, setDocumentsError] = useState('');
  const [selectedDocumentsCollection, setSelectedDocumentsCollection] = useState<string>('');
  const [editingDocumentId, setEditingDocumentId] = useState<string | null>(null);
  const [editingDocumentContent, setEditingDocumentContent] = useState('');
  const [isSavingDocument, setIsSavingDocument] = useState(false);

  useEffect(() => {
    if (selectedProjectId) {
      loadCollections();
    }
  }, [selectedProjectId]);

  const loadCollections = async () => {
    if (!selectedProjectId) {
      setCollectionError('Please select a project first');
      return;
    }
    
    setIsLoadingCollections(true);
    setCollectionError('');
    
    try {
      const response = await collectionsService.getDocumentCollections({ projectId: selectedProjectId });
      if (response.status === 'success' && Array.isArray(response.data)) {
        setCollections(response.data);
      } else {
        setCollectionError(response.message || 'Failed to load collections');
      }
    } catch (error) {
      setCollectionError('An unexpected error occurred');
    } finally {
      setIsLoadingCollections(false);
    }
  };

  const handleOpenDialog = (collection?: DocumentCollection) => {
    if (collection) {
      setEditingCollection(collection);
      setCollectionName(collection.name || '');
    } else {
      setEditingCollection(null);
      setCollectionName('');
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingCollection(null);
    setCollectionName('');
  };

  const handleSaveCollection = async () => {
    if (!collectionName.trim()) {
      return;
    }

    setIsSavingCollection(true);
    
    try {
      let response;
      if (editingCollection) {
        response = await collectionsService.updateDocumentCollection(editingCollection.uuid, { name: collectionName }, selectedProjectId!);
      } else {
        response = await collectionsService.createDocumentCollection({ name: collectionName, projectId: selectedProjectId! });
      }
      
      if (response.status === 'success') {
        await loadCollections();
        handleCloseDialog();
      } else {
        setCollectionError(response.message || 'Failed to save collection');
      }
    } catch (error) {
      setCollectionError('An unexpected error occurred');
    } finally {
      setIsSavingCollection(false);
    }
  };

  const handleDeleteCollection = async (id: string) => {
    if (!confirm('Are you sure you want to delete this collection?')) {
      return;
    }
    
    try {
      const response = await collectionsService.deleteDocumentCollection(id, selectedProjectId!);
      if (response.status === 'success') {
        await loadCollections();
      } else {
        setCollectionError(response.message || 'Failed to delete collection');
      }
    } catch (error) {
      setCollectionError('An unexpected error occurred');
    }
  };

  const handleChunkText = () => {
    if (!textContent.trim()) {
      setChunks([]);
      return;
    }
    
    let chunked: string[];
    if (chunkMethod === 'size') {
      chunked = vectorLoaderService.chunkText(textContent, chunkSize, chunkOverlap);
    } else {
      chunked = vectorLoaderService.chunkTextByDelimiter(textContent, delimiter);
    }
    
    setChunks(chunked);
  };

  const handleLoadChunks = async () => {
    if (chunks.length === 0) {
      setLoadError('No chunks to load. Please enter text and chunk it first.');
      return;
    }

    if (!selectedLoadCollection) {
      setLoadError('Please select a collection first.');
      return;
    }

    setIsLoading(true);
    setLoadMessage('');
    setLoadError('');

    try {
      const response = await vectorLoaderService.loadChunks({ 
        chunks,
        collectionName: selectedLoadCollection,
        projectId: selectedProjectId
      });
      
      if (response.success) {
        setLoadMessage(response.message || `Successfully loaded ${response.documentsCount} chunks`);
        setTextContent('');
        setChunks([]);
      } else {
        setLoadError(response.error || 'Failed to load chunks');
      }
    } catch (error) {
      setLoadError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const loadDocuments = async () => {
    if (!selectedDocumentsCollection) {
      setDocumentsError('Please select a collection first.');
      return;
    }

    setIsLoadingDocuments(true);
    setDocumentsError('');

    try {
      const offset = (documentsPage - 1) * documentsPerPage;
      const response = await vectorLoaderService.getDocuments(
        selectedDocumentsCollection,
        documentsPerPage,
        offset,
        selectedProjectId
      );

      if (response.success && response.documents) {
        setDocuments(response.documents);
        setDocumentsTotal(response.total || 0);
      } else {
        setDocumentsError(response.error || 'Failed to load documents');
      }
    } catch (error) {
      setDocumentsError('An unexpected error occurred');
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  const handleDeleteDocuments = async () => {
    if (selectedDocumentIds.size === 0) {
      setDocumentsError('Please select documents to delete');
      return;
    }

    if (!confirm(`Are you sure you want to delete ${selectedDocumentIds.size} document(s)?`)) {
      return;
    }

    setIsDeletingDocuments(true);
    setDocumentsError('');

    try {
      const response = await vectorLoaderService.bulkDeleteDocuments(
        selectedDocumentsCollection,
        Array.from(selectedDocumentIds),
        selectedProjectId
      );

      if (response.success) {
        setSelectedDocumentIds(new Set());
        await loadDocuments();
      } else {
        setDocumentsError(response.error || 'Failed to delete documents');
      }
    } catch (error) {
      setDocumentsError('An unexpected error occurred');
    } finally {
      setIsDeletingDocuments(false);
    }
  };

  const toggleDocumentSelection = (docId: string) => {
    const newSelection = new Set(selectedDocumentIds);
    if (newSelection.has(docId)) {
      newSelection.delete(docId);
    } else {
      newSelection.add(docId);
    }
    setSelectedDocumentIds(newSelection);
  };

  const toggleAllDocuments = () => {
    if (selectedDocumentIds.size === documents.length) {
      setSelectedDocumentIds(new Set());
    } else {
      const allIds = documents.map(doc => doc.metadata.id).filter(Boolean);
      setSelectedDocumentIds(new Set(allIds));
    }
  };

  const handleEditDocument = (doc: Document) => {
    setEditingDocumentId(doc.metadata.id);
    setEditingDocumentContent(doc.content);
  };

  const handleCancelEdit = () => {
    setEditingDocumentId(null);
    setEditingDocumentContent('');
  };

  const handleSaveDocument = async () => {
    if (!editingDocumentId || !selectedDocumentsCollection) return;

    setIsSavingDocument(true);
    setDocumentsError('');

    try {
      const response = await vectorLoaderService.updateDocument(
        selectedDocumentsCollection,
        editingDocumentId,
        editingDocumentContent,
        selectedProjectId
      );

      if (response.success) {
        // Update the document in the local state
        setDocuments(prevDocs => 
          prevDocs.map(doc => 
            doc.metadata.id === editingDocumentId 
              ? { ...doc, content: editingDocumentContent }
              : doc
          )
        );
        handleCancelEdit();
      } else {
        setDocumentsError(response.error || 'Failed to update document');
      }
    } catch (error) {
      setDocumentsError('An unexpected error occurred');
    } finally {
      setIsSavingDocument(false);
    }
  };

  useEffect(() => {
    if (selectedDocumentsCollection) {
      loadDocuments();
    }
  }, [selectedDocumentsCollection, documentsPage]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchError('Please enter a search query');
      return;
    }

    if (!selectedSearchCollection) {
      setSearchError('Please select a collection first.');
      return;
    }

    setIsSearching(true);
    setSearchError('');
    setSearchResults([]);

    try {
      const response = await vectorLoaderService.search({
        query: searchQuery,
        topK,
        collectionName: selectedSearchCollection,
        projectId: selectedProjectId
      });
      
      if (response.success && response.results) {
        setSearchResults(response.results);
      } else {
        setSearchError(response.error || 'Search failed');
      }
    } catch (error) {
      setSearchError('An unexpected error occurred during search');
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Vector Loader</h1>
        <p className="text-muted-foreground mt-2">
          Load and search text chunks using vector embeddings
        </p>
      </div>

      <Tabs defaultValue="collections" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="collections">Collections</TabsTrigger>
          <TabsTrigger value="load">Load Chunks</TabsTrigger>
          <TabsTrigger value="search">Search</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="collections" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Vector Collections</CardTitle>
                  <CardDescription>
                    Manage your vector collections
                  </CardDescription>
                </div>
                <Button onClick={() => handleOpenDialog()} size="sm">
                  <Plus className="mr-2 h-4 w-4" />
                  New Collection
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {collectionError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{collectionError}</AlertDescription>
                </Alert>
              )}
              
              {isLoadingCollections ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : collections.length === 0 ? (
                <div className="text-center py-8">
                  <Database className="mx-auto h-12 w-12 text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">No collections yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Create your first collection to get started
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {collections.map((collection) => (
                    <div
                      key={collection.uuid}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex-1">
                        <h4 className="font-medium">{collection.name || 'Unnamed Collection'}</h4>
                        <p className="text-sm text-muted-foreground">
                          ID: {collection.uuid}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => handleOpenDialog(collection)}
                          size="sm"
                          variant="ghost"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleDeleteCollection(collection.uuid)}
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="load" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Text Chunking</CardTitle>
              <CardDescription>
                Enter text to split into chunks for vector storage
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="load-collection">Vector Collection</Label>
                <Select value={selectedLoadCollection} onValueChange={setSelectedLoadCollection}>
                  <SelectTrigger id="load-collection" className="max-w-xs">
                    <SelectValue placeholder="Select a collection" />
                  </SelectTrigger>
                  <SelectContent>
                    {collections.map((collection) => (
                      <SelectItem key={collection.uuid} value={collection.name || collection.uuid}>
                        {collection.name || 'Unnamed Collection'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="chunk-method">Chunking Method</Label>
                <Select value={chunkMethod} onValueChange={(value: 'size' | 'delimiter') => setChunkMethod(value)}>
                  <SelectTrigger id="chunk-method" className='max-w-xs'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="size">By Size</SelectItem>
                    <SelectItem value="delimiter">By Delimiter</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {chunkMethod === 'size' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="chunk-size">Chunk Size</Label>
                    <Input
                      id="chunk-size"
                      type="number"
                      value={chunkSize}
                      onChange={(e) => setChunkSize(Number(e.target.value))}
                      min={100}
                      max={2000}
                    />
                  </div>
                  <div>
                    <Label htmlFor="chunk-overlap">Chunk Overlap</Label>
                    <Input
                      id="chunk-overlap"
                      type="number"
                      value={chunkOverlap}
                      onChange={(e) => setChunkOverlap(Number(e.target.value))}
                      min={0}
                      max={500}
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <Label htmlFor="delimiter">Delimiter</Label>
                  <Input
                    id="delimiter"
                    className='max-w-xs'
                    type="text"
                    value={delimiter}
                    onChange={(e) => setDelimiter(e.target.value)}
                    placeholder="Enter delimiter (e.g., ---, ===, ***)"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="text-content">Text Content</Label>
                <Textarea
                  id="text-content"
                  placeholder="Paste your text here..."
                  value={textContent}
                  onChange={(e) => setTextContent(e.target.value)}
                  rows={10}
                  className="font-mono"
                />
              </div>

              <Button 
                onClick={handleChunkText} 
                disabled={!textContent.trim()}
              >
                <FileText className="mr-2 h-4 w-4" />
                Create Chunks ({textContent.length} characters)
              </Button>
            </CardContent>
          </Card>

          {chunks.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Generated Chunks ({chunks.length})</CardTitle>
                <CardDescription>
                  Review the chunks before loading them into the vector store
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {chunks.map((chunk, index) => (
                    <div key={index} className="p-3 bg-muted rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">Chunk {index + 1}</span>
                        <span className="text-sm text-muted-foreground">
                          {chunk.length} chars
                        </span>
                      </div>
                      <p className="text-sm line-clamp-3">{chunk}</p>
                    </div>
                  ))}
                </div>

                {loadMessage && (
                  <Alert className="border-green-200 bg-green-50">
                    <AlertDescription className="text-green-800">
                      {loadMessage}
                    </AlertDescription>
                  </Alert>
                )}

                {loadError && (
                  <Alert variant="destructive">
                    <AlertDescription>{loadError}</AlertDescription>
                  </Alert>
                )}

                <Button 
                  onClick={handleLoadChunks}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Load Chunks into Vector Store
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="search" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Vector Search</CardTitle>
              <CardDescription>
                Search for similar content in the vector store
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="search-collection">Vector Collection</Label>
                <Select value={selectedSearchCollection} onValueChange={setSelectedSearchCollection}>
                  <SelectTrigger id="search-collection" className="max-w-xs">
                    <SelectValue placeholder="Select a collection" />
                  </SelectTrigger>
                  <SelectContent>
                    {collections.map((collection) => (
                      <SelectItem key={collection.uuid} value={collection.name || collection.uuid}>
                        {collection.name || 'Unnamed Collection'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="search-query">Search Query</Label>
                <div className="flex gap-2">
                  <Input
                    id="search-query"
                    placeholder="Enter your search query..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <Input
                    type="number"
                    value={topK}
                    onChange={(e) => setTopK(Number(e.target.value))}
                    min={1}
                    max={20}
                    className="w-20"
                    placeholder="Top K"
                  />
                </div>
              </div>

              <Button 
                onClick={handleSearch}
                disabled={isSearching || !searchQuery.trim()}
              >
                {isSearching ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="mr-2 h-4 w-4" />
                    Search
                  </>
                )}
              </Button>

              {searchError && (
                <Alert variant="destructive">
                  <AlertDescription>{searchError}</AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {searchResults.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Search Results ({searchResults.length})</CardTitle>
                <CardDescription>
                  Results sorted by similarity score
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {searchResults.map((result, index) => (
                    <div key={index} className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Result {index + 1}</span>
                        </div>
                        <span className="text-sm text-muted-foreground px-2 py-1 bg-muted rounded">
                          Score: {result.score.toFixed(4)}
                        </span>
                      </div>
                      <p className="text-sm">{result.content}</p>
                      {Object.keys(result.metadata).length > 0 && (
                        <div className="mt-2 pt-2 border-t">
                          <p className="text-xs text-muted-foreground">
                            Metadata: {JSON.stringify(result.metadata)}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="documents" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Document Management</CardTitle>
              <CardDescription>
                View and manage documents in your vector collections
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="documents-collection">Vector Collection</Label>
                <Select value={selectedDocumentsCollection} onValueChange={setSelectedDocumentsCollection}>
                  <SelectTrigger id="documents-collection" className="max-w-xs">
                    <SelectValue placeholder="Select a collection" />
                  </SelectTrigger>
                  <SelectContent>
                    {collections.map((collection) => (
                      <SelectItem key={collection.uuid} value={collection.name || collection.uuid}>
                        {collection.name || 'Unnamed Collection'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {documentsError && (
                <Alert variant="destructive">
                  <AlertDescription>{documentsError}</AlertDescription>
                </Alert>
              )}

              {selectedDocumentsCollection && (
                <>
                  {isLoadingDocuments ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : documents.length === 0 ? (
                    <div className="text-center py-8">
                      <FileX2 className="mx-auto h-12 w-12 text-muted-foreground mb-3" />
                      <p className="text-muted-foreground">No documents found</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Load some chunks into this collection first
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={loadDocuments}
                        className="mt-4"
                      >
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Refresh
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={selectedDocumentIds.size === documents.length && documents.length > 0}
                            onCheckedChange={toggleAllDocuments}
                          />
                          <span className="text-sm text-muted-foreground">
                            {selectedDocumentIds.size > 0 
                              ? `${selectedDocumentIds.size} selected` 
                              : 'Select all'}
                          </span>
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={handleDeleteDocuments}
                          disabled={selectedDocumentIds.size === 0 || isDeletingDocuments}
                        >
                          {isDeletingDocuments ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Deleting...
                            </>
                          ) : (
                            <>
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete Selected ({selectedDocumentIds.size})
                            </>
                          )}
                        </Button>
                      </div>

                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {documents.map((doc, index) => (
                          <div
                            key={doc.metadata.id || index}
                            className="flex items-start gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                          >
                            <Checkbox
                              checked={selectedDocumentIds.has(doc.metadata.id)}
                              onCheckedChange={() => toggleDocumentSelection(doc.metadata.id)}
                              disabled={!doc.metadata.id || editingDocumentId === doc.metadata.id}
                            />
                            <div className="flex-1">
                              {editingDocumentId === doc.metadata.id ? (
                                <div className="space-y-2">
                                  <Textarea
                                    value={editingDocumentContent}
                                    onChange={(e) => setEditingDocumentContent(e.target.value)}
                                    className="min-h-[100px] text-sm"
                                    placeholder="Enter document content..."
                                  />
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      onClick={handleSaveDocument}
                                      disabled={isSavingDocument}
                                    >
                                      {isSavingDocument ? (
                                        <>
                                          <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                                          Saving...
                                        </>
                                      ) : (
                                        'Save'
                                      )}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={handleCancelEdit}
                                      disabled={isSavingDocument}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <p className="text-sm line-clamp-3">{doc.content}</p>
                                  <div className="flex items-center justify-between mt-1">
                                    <p className="text-xs text-muted-foreground">
                                      ID: {doc.metadata.id || 'N/A'}
                                    </p>
                                    {doc.metadata.id && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleEditDocument(doc)}
                                        className="h-6 px-2"
                                      >
                                        <Edit2 className="h-3 w-3" />
                                      </Button>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">
                          Showing {((documentsPage - 1) * documentsPerPage) + 1}-{Math.min(documentsPage * documentsPerPage, documentsTotal)} of {documentsTotal}
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDocumentsPage(p => Math.max(1, p - 1))}
                            disabled={documentsPage === 1}
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <span className="text-sm">
                            Page {documentsPage} of {Math.ceil(documentsTotal / documentsPerPage)}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDocumentsPage(p => p + 1)}
                            disabled={documentsPage >= Math.ceil(documentsTotal / documentsPerPage)}
                          >
                            <ChevronRightIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCollection ? 'Edit Collection' : 'Create Collection'}
            </DialogTitle>
            <DialogDescription>
              {editingCollection
                ? 'Update the collection name'
                : 'Enter a name for your new vector collection'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="collection-name">Collection Name</Label>
            <Input
              id="collection-name"
              value={collectionName}
              onChange={(e) => setCollectionName(e.target.value)}
              placeholder="Enter collection name..."
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveCollection}
              disabled={isSavingCollection || !collectionName.trim()}
            >
              {isSavingCollection ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                editingCollection ? 'Update' : 'Create'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}